import useApiCall, {makeApiCall} from "../../hooks/CancellableApiCall";
import {LoadingContent} from "../LoadingContent";
import React, {useCallback, useEffect, useState} from "react";
import {
    Alert,
    Box,
    Button,
    IconButton,
    Link,
    Pagination,
    Paper,
    Snackbar,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip
} from "@mui/material";
import {Link as RouterLink, useSearchParams} from "react-router-dom";
import {PaginatedUserSummaryResponseWithActivity, UserSummaryResponseWithActivity} from "../../data/UserSummary";
import {formatDate, pluralize} from "../../utils";
import WarningIcon from '@mui/icons-material/Warning';
import {AlertType} from "../../data/AlertType";
import {ConfirmationDialog} from "../ConfirmationDialog";
import {InactivityNoticeApprovalResponse} from "../../data/InactivityNoticeApprovalResponse";

interface ActivityProps {
    divisionIdentifier: string
}

export interface ActivityRowProps {
    member: UserSummaryResponseWithActivity
    divisionIdentifier: string
}

const ActivityRow = (props: ActivityRowProps) => {
    const [showAlert, setShowAlert] = useState(false)
    const [alertType, setAlertType] = useState<AlertType>("success")
    const [alertText, setAlertText] = useState("")
    const setAlert = useCallback((type: AlertType, text: string) => {
        setAlertType(type)
        setAlertText(text)
        setShowAlert(true)
    }, [setAlertType, setAlertText, setShowAlert])
    const [openConfirm, setOpenConfirm] = useState(false)

    const issueActivityWarning = (confirm: Boolean) => {
        setOpenConfirm(false)
        if (confirm) {
            makeApiCall({
                url: `/api/division/${props.divisionIdentifier}/activity`,
                method: "POST",
                body: {
                    employeeCommunityId: props.member.communityId
                },
                onLoadedCallback: () => {
                    setAlert("success", "Created inactivity warning")
                },
                onError: () => {
                    setAlert("error", "Failed to create inactivity warning")
                }
            })
        }
    }

    return <TableRow>
        <TableCell>
            <Link underline="none" component={RouterLink} to={`/user/${props.member.communityId}`}>
            {props.member.nick}
            </Link>
        </TableCell>
        <TableCell>
            <Link underline="none" component={RouterLink} to={`/user/${props.member.communityId}`}>
                {props.member.rpName}
            </Link>
        </TableCell>
        <TableCell>{props.member.badgeNumber}</TableCell>
        <TableCell>{props.member.divisionRoleDisplayName}</TableCell>
        <TableCell>{Math.floor(props.member.thisMonthsActivity / 60 / 60)} {pluralize("Hour", props.member.thisMonthsActivity / 60 / 60)}</TableCell>
        {props.member.lastMonthsActivity >= 7200 ? <>
                <TableCell>{props.member.lastMonthsActivity ? <>
                    {Math.floor(props.member.lastMonthsActivity / 60 / 60)} {pluralize("Hour", props.member.lastMonthsActivity / 60 / 60)}
                </> : "Inactive"}</TableCell>
                <TableCell>
                    <IconButton disabled={true} size="small" color="error">
                        <WarningIcon color={"disabled"} fontSize="small" />
                    </IconButton>
                </TableCell>
            </>
            :
            <>
                <TableCell sx={{backgroundColor: "darkred", color: "white"}}>{props.member.lastMonthsActivity ? <>
                    {Math.floor(props.member.lastMonthsActivity / 60 / 60)} {pluralize("Hour", props.member.lastMonthsActivity / 60 / 60)} Fails to meet minimum hours
                </> : "Inactive"}</TableCell>
                <TableCell sx={{backgroundColor: "darkred"}}>
                    <Tooltip title={"Issue Activity Warning"}>
                        <IconButton size="small" color="error" onClick={() =>
                            setOpenConfirm(true)
                        }>
                            <WarningIcon color={"warning"} fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </TableCell>
            </>
        }
        <Snackbar open={showAlert} autoHideDuration={5000} onClose={() => setShowAlert(false)}>
            <Alert severity={alertType} onClose={() => setShowAlert(false)} sx={{width: "100%"}} variant="filled">
                {alertText}
            </Alert>
        </Snackbar>
        <ConfirmationDialog title={"Issue Activity Warning"} description={"This will make a inbox and issue a record note"} open={openConfirm} onClose={issueActivityWarning} />
    </TableRow>
}

export const Activity = (props: ActivityProps) => {
    const [searchParams, setSearchParams] = useSearchParams()
    const page = parseInt(searchParams.get("page") ?? "1")
    const [activity, setActivity] = useState<PaginatedUserSummaryResponseWithActivity>({
        members: [],
        count: 0,
        pages: 1
    })
    const loadLOARequests = useApiCall<InactivityNoticeApprovalResponse[]>({
        initialUrl: `/api/division/${props.divisionIdentifier}/loa-requests/view`
    })

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(`/api/division/${props.divisionIdentifier}/activity/${page}`)
                const data = await response.json()
                setActivity(data)
            } catch (error) {
                console.error('Error fetching Rows:', error)
            }
        };
        fetchData()
    }, [page, props.divisionIdentifier])

    const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
        searchParams.set("page", value.toString())
        setSearchParams(searchParams)
    }

    const handleActivityRequest = (id: number, decision: boolean) => {
        makeApiCall({
            url: `/api/division/${props.divisionIdentifier}/loa-requests/handle`,
            method: "POST",
            body: {
                approvalId: id,
                decision: decision
            },
            onLoadedCallback: () => {
                loadLOARequests.refresh()
            },
            onError: () => {}
        })
    }

    return <Stack spacing={6}>
        {
            loadLOARequests.data?.length ?? 0 >= 1 ? <>
                <Alert color={"info"}>There are pending LOA requests which you can review</Alert>
                <Paper variant={"outlined"} sx={{p: 2, overflowX: "auto"}}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Employee</TableCell>
                                <TableCell>Reason</TableCell>
                                <TableCell>Start Date</TableCell>
                                <TableCell>End Date</TableCell>
                                <TableCell>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {
                                loadLOARequests.data && loadLOARequests.data.map((request) => {
                                    return <React.Fragment key={request.id}>
                                        <TableRow>
                                            <TableCell>
                                                <Link underline="none" component={RouterLink} to={`/user/${request.communityId}`}>
                                                    {request.nick}
                                                </Link>
                                            </TableCell>
                                            <TableCell>{request.note}</TableCell>
                                            <TableCell>{formatDate(request.startDate)}</TableCell>
                                            <TableCell>{formatDate(request.endDate)}</TableCell>
                                            <TableCell>
                                                <Stack spacing={2} direction={"row"}>
                                                    <Button color={"success"} variant={"contained"} onClick={() => {
                                                        handleActivityRequest(request.id, true)
                                                    }}>Accept</Button>
                                                    <Button color={"error"} variant={"contained"} onClick={() => {
                                                        handleActivityRequest(request.id, false)
                                                    }}>Deny</Button>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                })
                            }
                        </TableBody>
                    </Table>
                </Paper>
            </>: <></>
        }
            <Paper variant={"outlined"} sx={{p: 2, overflowX: "auto"}}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Steam Name</TableCell>
                            <TableCell>In-Game Name</TableCell>
                            <TableCell>Badge Number</TableCell>
                            <TableCell>Division Rank</TableCell>
                            <TableCell>Activity</TableCell>
                            <TableCell>Activity Last Month</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <LoadingContent showLoadingSkeleton={true} isLoading={activity.count === 0}>
                            {activity.members && activity.members.map((member) => {
                                return <ActivityRow key={member.communityId} divisionIdentifier={props.divisionIdentifier} member={member}/>
                            })}
                        </LoadingContent>
                    </TableBody>
                </Table>
                <Box sx={{justifyContent: "center", display: "flex", mt: 2, width: "100%"}}>
                    <Pagination color={'secondary'} count={activity.pages} page={page} style={{padding: 8}}
                                onChange={handleChange}/>
                </Box>
            </Paper>
        </Stack>
}